import { useUpdate } from 'react-admin';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import {
  useRecordContext,
  useRefresh,
  useGetIdentity,
  useGetList,
  Labeled,
  UpdateButton,
} from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import { adminRpcClient } from '@/providers/backendClient';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { FieldWithAction } from '@/components/FieldWithAction';

const customPromptAudioSupportModeOptions = [
  'disabled',
  'inline',
  'exclusive',
] as const;
type CustomPromptAudioSupportMode =
  (typeof customPromptAudioSupportModeOptions)[number];

// Numeric field dialog component
const NumericFieldDialog = ({
  open,
  onClose,
  label,
  value,
  onSave,
  isLoading,
  helperText,
  inputProps,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  value: number | null;
  onSave: (value: number | null) => Promise<void>;
  isLoading: boolean;
  helperText?: string;
  inputProps?: any;
}) => {
  const [localValue, setLocalValue] = useState<string>(
    value === null ? '' : value.toString(),
  );

  const handleSave = useCallback(async () => {
    const numValue =
      localValue === '' || localValue === null
        ? null
        : Number(localValue);
    if (!isNaN(numValue as number) || numValue === null) {
      await onSave(numValue);
      onClose();
    }
  }, [localValue, onSave, onClose]);

  const handleClose = useCallback(() => {
    setLocalValue(value === null ? '' : value.toString());
    onClose();
  }, [value, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={label}
          type="number"
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          helperText={helperText}
          inputProps={inputProps}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AppConfigOverrides = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();
  const [updateTherapist, { isLoading: isSaving }] = useUpdate(
    'therapists',
    { id: record.id },
  );

  // Dialog states for numeric fields
  const [numericDialog, setNumericDialog] = useState<{
    field: string;
    open: boolean;
  } | null>(null);

  // Individual field update handlers
  const updateField = useCallback(
    async (field: string, value: any) => {
      await updateTherapist(
        'therapists',
        {
          id: record.id,
          data: {
            [field]: value,
            lastUpdatedAt: new Date(),
            lastUpdatedBy: identity?.id,
          },
        },
        {
          onSuccess: () => {
            refresh();
          },
        },
      );
    },
    [updateTherapist, record.id, identity?.id, refresh],
  );

  // Numeric field handlers
  const openNumericDialog = useCallback((field: string) => {
    setNumericDialog({ field, open: true });
  }, []);

  const closeNumericDialog = useCallback(() => {
    setNumericDialog(null);
  }, []);

  const saveNumericField = useCallback(
    async (field: string, value: number | null) => {
      await updateField(field, value);
    },
    [updateField],
  );

  // Admin Config handler (special case - uses RPC)
  const {
    mutate: toggleHiddenTestAccount,
    isPending: isTogglingHidden,
  } = useMutation({
    mutationKey: [
      'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
      record.id,
      record.isHiddenTestAccount,
    ],
    mutationFn: async () => {
      await adminRpcClient(
        'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
        [record.id, !Boolean(record.isHiddenTestAccount)],
      );
    },
    onSettled: refresh,
  });

  const { data: modelsList } = useGetList('llmModels', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const models = Array.isArray(modelsList) ? modelsList : [];
  const processingModels = models.filter(
    (m: { purpose: string }) => m.purpose === 'processing',
  );
  const transcriptModels = models.filter(
    (m: { purpose: string }) => m.purpose === 'transcript',
  );
  const savedDefaultModelId =
    (record as { defaultModelId?: string | null })?.defaultModelId ??
    null;
  const savedSelect =
    savedDefaultModelId === null || savedDefaultModelId === ''
      ? '__global__'
      : savedDefaultModelId;

  const [pendingLlmSelect, setPendingLlmSelect] =
    useState(savedSelect);
  const [llmConfirmOpen, setLlmConfirmOpen] = useState(false);

  const savedDefaultTranscriptModelId =
    (record as { defaultTranscriptModelId?: string | null })
      ?.defaultTranscriptModelId ?? null;
  const savedTranscriptSelect =
    savedDefaultTranscriptModelId === null ||
    savedDefaultTranscriptModelId === ''
      ? '__global__'
      : savedDefaultTranscriptModelId;

  const [pendingTranscriptSelect, setPendingTranscriptSelect] = useState(
    savedTranscriptSelect,
  );
  const [transcriptConfirmOpen, setTranscriptConfirmOpen] = useState(false);

  const getModelName = (
    list: typeof models,
    id: string,
  ) =>
    list.find(
      (m: { id?: string; _id?: string; name: string }) =>
        (m.id ?? m._id) === id,
    )?.name ?? id;

  const hasLlmChanges = pendingLlmSelect !== savedSelect;
  const pendingLlmName =
    pendingLlmSelect === '__global__'
      ? 'Use global default'
      : getModelName(processingModels, pendingLlmSelect);
  const savedLlmName =
    savedSelect === '__global__'
      ? 'Use global default'
      : getModelName(processingModels, savedSelect);

  const hasTranscriptChanges =
    pendingTranscriptSelect !== savedTranscriptSelect;
  const pendingTranscriptName =
    pendingTranscriptSelect === '__global__'
      ? 'Use global default'
      : getModelName(transcriptModels, pendingTranscriptSelect);
  const savedTranscriptName =
    savedTranscriptSelect === '__global__'
      ? 'Use global default'
      : getModelName(transcriptModels, savedTranscriptSelect);

  const handleLlmSaveClick = useCallback(() => {
    setLlmConfirmOpen(true);
  }, []);

  const handleLlmConfirmClose = useCallback(() => {
    setLlmConfirmOpen(false);
  }, []);

  const handleLlmConfirmSave = useCallback(async () => {
    setLlmConfirmOpen(false);
    const value =
      pendingLlmSelect === '__global__' ? null : pendingLlmSelect;
    await updateField('defaultModelId', value);
  }, [updateField, pendingLlmSelect]);

  const handleTranscriptSaveClick = useCallback(() => {
    setTranscriptConfirmOpen(true);
  }, []);

  const handleTranscriptConfirmClose = useCallback(() => {
    setTranscriptConfirmOpen(false);
  }, []);

  const handleTranscriptConfirmSave = useCallback(async () => {
    setTranscriptConfirmOpen(false);
    const value =
      pendingTranscriptSelect === '__global__'
        ? null
        : pendingTranscriptSelect;
    await updateField('defaultTranscriptModelId', value);
  }, [updateField, pendingTranscriptSelect]);

  useEffect(() => {
    setPendingLlmSelect(savedSelect);
  }, [savedSelect]);

  useEffect(() => {
    setPendingTranscriptSelect(savedTranscriptSelect);
  }, [savedTranscriptSelect]);

  return (
    <>
      <CollapsibleSection title="App Config Overrides">
        <Box display="flex" flexDirection="column" gap="1.5rem">
          {/* LLM model override */}
          <Box>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              LLM model
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ marginBottom: 1 }}
            >
              Override the default GPT/LLM model for this therapist.
            </Typography>
            <FormControl
              size="small"
              disabled={isSaving}
              sx={{ minWidth: 220 }}
            >
              <InputLabel>Model</InputLabel>
              <Select
                value={pendingLlmSelect}
                label="Model"
                onChange={e => setPendingLlmSelect(e.target.value)}
              >
                <MenuItem value="__global__">
                  Use global default
                </MenuItem>
                {processingModels.map(
                  (m: {
                    id?: string;
                    _id?: string;
                    name: string;
                  }) => (
                    <MenuItem
                      key={m.id ?? m._id}
                      value={m.id ?? m._id}
                    >
                      {m.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            {hasLlmChanges && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={handleLlmSaveClick}
                >
                  Save
                </Button>
              </Box>
            )}
            <Dialog
              open={llmConfirmOpen}
              onClose={handleLlmConfirmClose}
            >
              <DialogTitle>Change LLM model override?</DialogTitle>
              <DialogContent>
                <Typography>
                  {savedSelect === '__global__'
                    ? `Set this therapist's model to "${pendingLlmName}"? They will use this model instead of the global default.`
                    : pendingLlmSelect === '__global__'
                      ? `Clear the override (currently "${savedLlmName}")? This therapist will use the global default model.`
                      : `Change this therapist's model from "${savedLlmName}" to "${pendingLlmName}"?`}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleLlmConfirmClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleLlmConfirmSave}
                  color="primary"
                  variant="contained"
                  disabled={isSaving}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          {/* Transcription model override */}
          <Box>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              Transcription model
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ marginBottom: 1 }}
            >
              Override the default speech-to-text model for this therapist
              (llmModels with purpose &quot;transcript&quot;).
            </Typography>
            <FormControl
              size="small"
              disabled={isSaving}
              sx={{ minWidth: 220 }}
            >
              <InputLabel>Transcript model</InputLabel>
              <Select
                value={pendingTranscriptSelect}
                label="Transcript model"
                onChange={e =>
                  setPendingTranscriptSelect(e.target.value)
                }
              >
                <MenuItem value="__global__">
                  Use global default
                </MenuItem>
                {transcriptModels.map(
                  (m: {
                    id?: string;
                    _id?: string;
                    name: string;
                  }) => (
                    <MenuItem
                      key={m.id ?? m._id}
                      value={m.id ?? m._id}
                    >
                      {m.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            {hasTranscriptChanges && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={handleTranscriptSaveClick}
                >
                  Save
                </Button>
              </Box>
            )}
            <Dialog
              open={transcriptConfirmOpen}
              onClose={handleTranscriptConfirmClose}
            >
              <DialogTitle>
                Change transcription model override?
              </DialogTitle>
              <DialogContent>
                <Typography>
                  {savedTranscriptSelect === '__global__'
                    ? `Set this therapist's transcription model to "${pendingTranscriptName}"? They will use this model instead of the global default.`
                    : pendingTranscriptSelect === '__global__'
                      ? `Clear the override (currently "${savedTranscriptName}")? This therapist will use the global default transcription model.`
                      : `Change this therapist's transcription model from "${savedTranscriptName}" to "${pendingTranscriptName}"?`}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleTranscriptConfirmClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleTranscriptConfirmSave}
                  color="primary"
                  variant="contained"
                  disabled={isSaving}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          {/* Debug Mode */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">Debug Mode</Typography>
              <Tooltip title="Enable or disable debug menu access for this therapist">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
            >
              <Labeled label="Debug Menu Enabled">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.isTestTherapist
                        ? 'Disable Debug Menu'
                        : 'Enable Debug Menu'
                    }
                    type="button"
                    color={
                      record.isTestTherapist ? 'error' : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.isTestTherapist
                        ? 'This will Disable Debug Menu access for this user'
                        : 'This will Enable Debug Menu access for this user'
                    }
                    icon={
                      record.isTestTherapist ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{ onSuccess: refresh }}
                    data={{
                      isTestTherapist: !record.isTestTherapist,
                      ...(record.isTestTherapist && {
                        advancedDebugMenuEnabled: false,
                      }),
                    }}
                  />
                </Box>
              </Labeled>
              {record.isTestTherapist && (
                <Labeled label="Advanced Debug Mode">
                  <Box sx={{ display: 'inline-block' }}>
                    <UpdateButton
                      resource="therapists"
                      label={
                        record.advancedDebugMenuEnabled
                          ? 'Disable Advanced Debug Mode'
                          : 'Enable Advanced Debug Mode'
                      }
                      type="button"
                      color={
                        record.advancedDebugMenuEnabled
                          ? 'error'
                          : 'warning'
                      }
                      variant="contained"
                      mutationMode="pessimistic"
                      confirmTitle="Are you sure?"
                      confirmContent={
                        record.advancedDebugMenuEnabled
                          ? 'This will Disable Advanced Debug Mode access for this user'
                          : 'This will Enable Advanced Debug Mode access for this user'
                      }
                      icon={
                        record.advancedDebugMenuEnabled ? (
                          <ClearIcon />
                        ) : (
                          <CheckIcon />
                        )
                      }
                      sx={{ color: '#fff', width: 'fit-content' }}
                      mutationOptions={{ onSuccess: refresh }}
                      data={{
                        advancedDebugMenuEnabled:
                          !record.advancedDebugMenuEnabled,
                      }}
                    />
                  </Box>
                </Labeled>
              )}
              <Labeled label="Show last archived global alert">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.debug_showLastArchivedGlobalAlert
                        ? 'Disable Show Last Archived Global Alert'
                        : 'Enable Show Last Archived Global Alert'
                    }
                    type="button"
                    color={
                      record.debug_showLastArchivedGlobalAlert
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.debug_showLastArchivedGlobalAlert
                        ? 'This will stop showing the last archived global alert for this user.'
                        : 'This will show the last global alert even when archived (for debugging).'
                    }
                    icon={
                      record.debug_showLastArchivedGlobalAlert ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{ onSuccess: refresh }}
                    data={{
                      debug_showLastArchivedGlobalAlert:
                        !record.debug_showLastArchivedGlobalAlert,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Modern Agent Prompts">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_modernAgentPrompts
                        ? 'Disable Modern Agent Prompts'
                        : 'Enable Modern Agent Prompts'
                    }
                    type="button"
                    color={
                      record.ff_modernAgentPrompts
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_modernAgentPrompts
                        ? 'This will disable the modern combined prompt structure for this user.'
                        : 'This will enable the modern combined prompt structure for this user.'
                    }
                    icon={
                      record.ff_modernAgentPrompts ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_modernAgentPrompts:
                        !record.ff_modernAgentPrompts,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Client Pronouns">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_promptAddClientPronounsAfterSessionNote
                        ? 'Disable Client Pronouns'
                        : 'Enable Client Pronouns'
                    }
                    type="button"
                    color={
                      record.ff_promptAddClientPronounsAfterSessionNote
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_promptAddClientPronounsAfterSessionNote
                        ? 'This will stop prompting this therapist to add client pronouns after committing a session note when the client has none set.'
                        : 'This will prompt this therapist to add client pronouns after committing a session note when the client has none set (if enabled globally).'
                    }
                    icon={
                      record.ff_promptAddClientPronounsAfterSessionNote ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_promptAddClientPronounsAfterSessionNote:
                        record.ff_promptAddClientPronounsAfterSessionNote
                          ? 0
                          : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Insurance Company Filter (FTIC)">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_insuranceCompanyFilter
                        ? 'Disable Insurance Note Check'
                        : 'Enable Insurance Note Check'
                    }
                    type="button"
                    color={
                      record.ff_insuranceCompanyFilter
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_insuranceCompanyFilter
                        ? 'This will hide the Insurance Note Check feature for this therapist.'
                        : 'This will show the Insurance Note Check feature for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_insuranceCompanyFilter ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_insuranceCompanyFilter:
                        record.ff_insuranceCompanyFilter ? 0 : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Magic Edit">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_magicEdit
                        ? 'Disable Magic Edit'
                        : 'Enable Magic Edit'
                    }
                    type="button"
                    color={record.ff_magicEdit ? 'error' : 'warning'}
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_magicEdit
                        ? 'This will hide the Magic Edit feature for this therapist.'
                        : 'This will show the Magic Edit feature for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_magicEdit ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_magicEdit: record.ff_magicEdit ? 0 : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Therapists web — Magic Edit">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_web_magicEdit
                        ? 'Disable web Magic Edit'
                        : 'Enable web Magic Edit'
                    }
                    type="button"
                    color={
                      record.ff_web_magicEdit ? 'error' : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_web_magicEdit
                        ? 'This will hide Magic Edit on therapists-web for this therapist.'
                        : 'This will show Magic Edit on therapists-web for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_web_magicEdit ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_web_magicEdit: record.ff_web_magicEdit
                        ? 0
                        : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Therapists web — Reprocess note">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_web_reprocessing
                        ? 'Disable web Reprocess'
                        : 'Enable web Reprocess'
                    }
                    type="button"
                    color={
                      record.ff_web_reprocessing ? 'error' : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_web_reprocessing
                        ? 'This will hide Reprocess on therapists-web for this therapist.'
                        : 'This will show Reprocess on therapists-web for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_web_reprocessing ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_web_reprocessing: record.ff_web_reprocessing
                        ? 0
                        : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Custom Reports">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_customReports
                        ? 'Disable Custom Reports'
                        : 'Enable Custom Reports'
                    }
                    type="button"
                    color={
                      record.ff_customReports ? 'error' : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_customReports
                        ? 'This will hide custom report prompts and format selection for this therapist.'
                        : 'This will show custom report prompts and format selection for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_customReports ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_customReports: record.ff_customReports
                        ? 0
                        : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Therapists web — Custom Reports">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_web_customReports
                        ? 'Disable web Custom Reports'
                        : 'Enable web Custom Reports'
                    }
                    type="button"
                    color={
                      record.ff_web_customReports
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_web_customReports
                        ? 'This will hide Custom Reports on therapists-web for this therapist.'
                        : 'This will show Custom Reports on therapists-web for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_web_customReports ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_web_customReports:
                        record.ff_web_customReports ? 0 : 1,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Custom prompt audio support">
                <Box sx={{ display: 'inline-block' }}>
                  <FormControl
                    size="small"
                    disabled={isSaving}
                    sx={{ minWidth: 220 }}
                  >
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={
                        (record.ff_customPromptAudioSupportMode ??
                          '__global__') as string
                      }
                      label="Mode"
                      onChange={event => {
                        const value = event.target.value;
                        void updateField(
                          'ff_customPromptAudioSupportMode',
                          value === '__global__'
                            ? null
                            : (value as CustomPromptAudioSupportMode),
                        );
                      }}
                    >
                      <MenuItem value="__global__">
                        Use global mode
                      </MenuItem>
                      {customPromptAudioSupportModeOptions.map(mode => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Labeled>
            </Box>
          </Box>

          {/* OTA Updates */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">OTA Updates</Typography>
              <Tooltip title="Force mandatory OTA updates for this therapist">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Labeled label="Mandatory OTA Updates">
              <Box sx={{ display: 'inline-block' }}>
                <UpdateButton
                  resource="therapists"
                  label={
                    record.otaUpdatesMandatory
                      ? 'Disable Mandatory OTA Updates'
                      : 'Force OTA Updates'
                  }
                  type="button"
                  color={
                    record.otaUpdatesMandatory ? 'error' : 'warning'
                  }
                  variant="contained"
                  mutationMode="pessimistic"
                  confirmTitle="Are you sure?"
                  confirmContent={
                    record.otaUpdatesMandatory
                      ? 'This will Disable Mandatory OTA Updates for this therapist. This can be overridden by the global OTA settings.'
                      : 'This will Force the Therapist to update their app as soon as an OTA is available'
                  }
                  icon={
                    record.otaUpdatesMandatory ? (
                      <ClearIcon />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  sx={{ color: '#fff', width: 'fit-content' }}
                  mutationOptions={{ onSuccess: refresh }}
                  data={{
                    otaUpdatesMandatory: !record.otaUpdatesMandatory,
                  }}
                />
              </Box>
            </Labeled>
          </Box>

          {/* Admin Config */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">Admin Config</Typography>
              <Tooltip title="Add or remove this therapist from hidden test accounts">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Labeled label="Hidden Test Account">
              <Box sx={{ display: 'inline-block' }}>
                <Button
                  color={
                    !record.isHiddenTestAccount ? 'warning' : 'error'
                  }
                  variant="contained"
                  sx={{ color: '#fff', width: 'fit-content' }}
                  size="small"
                  disabled={isTogglingHidden}
                  startIcon={
                    isTogglingHidden ? (
                      <CircularProgress size={18} />
                    ) : record.isHiddenTestAccount ? (
                      <ClearIcon />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={() => toggleHiddenTestAccount()}
                >
                  {record.isHiddenTestAccount
                    ? 'Remove From Hidden Test Accounts'
                    : 'Add to Hidden Test Accounts'}
                </Button>
              </Box>
            </Labeled>
          </Box>

          {/* App Behavior Fields */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">App Behavior</Typography>
              <Tooltip title="Override app behavior configuration values. Leave empty to use global config.">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
            >
              <Labeled label="Free Trial Popup Placement">
                <FieldWithAction
                  value={
                    record.freeTrialPopupPlacement?.toString() || null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Number of remaining free trial notes when popup should appear. Leave empty to use global config. Use 0 or negative to disable.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog('freeTrialPopupPlacement')
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="App Review Request Placement">
                <FieldWithAction
                  value={
                    record.appReviewRequestPlacement?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Number indicating when to request app review. Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'appReviewRequestPlacement',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="Global Tracing Sample Rate">
                <FieldWithAction
                  value={
                    record.tracing_sampleRate_global?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Global tracing sample rate (0.0 to 1.0). Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'tracing_sampleRate_global',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="Local Tracing Sample Rate">
                <FieldWithAction
                  value={
                    record.tracing_sampleRate_local?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Local tracing sample rate (0.0 to 1.0). Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'tracing_sampleRate_local',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
            </Box>
          </Box>
        </Box>
      </CollapsibleSection>

      {/* Numeric Field Dialogs */}
      {numericDialog && (
        <NumericFieldDialog
          open={numericDialog.open}
          onClose={closeNumericDialog}
          label={
            numericDialog.field === 'freeTrialPopupPlacement'
              ? 'Free Trial Popup Placement'
              : numericDialog.field === 'appReviewRequestPlacement'
                ? 'App Review Request Placement'
                : numericDialog.field === 'tracing_sampleRate_global'
                  ? 'Global Tracing Sample Rate'
                  : 'Local Tracing Sample Rate'
          }
          value={
            record[
              numericDialog.field as
                | 'freeTrialPopupPlacement'
                | 'appReviewRequestPlacement'
                | 'tracing_sampleRate_global'
                | 'tracing_sampleRate_local'
            ] ?? null
          }
          onSave={value =>
            saveNumericField(numericDialog.field, value)
          }
          isLoading={isSaving}
          helperText={
            numericDialog.field === 'freeTrialPopupPlacement'
              ? 'Number of remaining free trial notes when popup should appear. Leave empty to use global config. Use 0 or negative to disable.'
              : numericDialog.field === 'appReviewRequestPlacement'
                ? 'Number indicating when to request app review. Leave empty to use global config.'
                : numericDialog.field === 'tracing_sampleRate_global'
                  ? 'Global tracing sample rate (0.0 to 1.0). Leave empty to use global config.'
                  : 'Local tracing sample rate (0.0 to 1.0). Leave empty to use global config.'
          }
          inputProps={
            numericDialog.field.includes('tracing_sampleRate')
              ? { step: 0.01, min: 0, max: 1 }
              : { step: 1 }
          }
        />
      )}
    </>
  );
};
